import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GOAL_STATUSES,
  GOAL_STATUS_MAP,
  MAX_PLAN_OBJECTIVES,
  PLAN_STATUSES,
  PLAN_STATUS_MAP,
  SUGGESTED_CRITERIA,
  type GoalStatus,
  type PlanStatus,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api, qs } from '../../lib/api';
import { date, num, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';

/* ==========================================================================
   الصفوف كما يرسلها الخادم
   ========================================================================== */

export interface CycleRow {
  id: string;
  name: string;
  status: 'DRAFT' | 'OPEN' | 'CLOSED';
  statusLabel: string;
  statusKind: string;
  statusNote: string;
  writable: boolean;
  startsOn: string | null;
  endsOn: string | null;
  dueOn: string | null;
  scaleMax: number;
  notes: string | null;
  criteriaCount: number;
  reviews: number;
  goals: number;
}

export interface GoalRow {
  id: string;
  title: string;
  description: string | null;
  metric: string | null;
  target: string | null;
  weight: number;
  progress: number;
  status: GoalStatus;
  statusLabel: string;
  statusKind: string;
  live: boolean;
  dueOn: string | null;
  outcome: string | null;
  overdue: boolean;
  employee: { id: string; name: string; code: string | null };
  cycle: { id: string; name: string } | null;
}

export interface PlanRow {
  id: string;
  reason: string;
  startsOn: string | null;
  endsOn: string | null;
  objectives: { text: string; done: boolean }[];
  done: number;
  status: PlanStatus;
  statusLabel: string;
  statusKind: string;
  statusNote: string;
  live: boolean;
  outcome: string | null;
  daysLeft: number | null;
  employee: { id: string; name: string; code: string | null };
  owner: { id: string; name: string } | null;
}

interface Summary {
  cycles: number;
  reviews: number;
  openGoals: number;
  openPlans: number;
  progress: {
    cycleId: string;
    name: string;
    expected: number;
    written: number;
    submitted: number;
  } | null;
}

export interface EmployeeLite {
  id: string;
  name: string;
  code: string | null;
}

type Tab = 'cycles' | 'goals' | 'plans';

const TABS: readonly { key: Tab; label: string }[] = [
  { key: 'cycles', label: 'دورات التقييم' },
  { key: 'goals', label: 'الأهداف' },
  { key: 'plans', label: 'خطط التحسين' },
] as const;

/* ==========================================================================
   الصفحة
   ========================================================================== */

/**
 * إدارة الأداء.
 *
 * ## دورةٌ، وأهداف، وتقييم، وخطّة
 *
 * الدورةُ مدّةٌ تُقاس ومعاييرُ تُقاس بها. تُعدّ أوّلًا فتُحرَّر معاييرُها،
 * ثمّ تُفتح فتُجمَّد وتُكتب فيها التقييمات، ثمّ تُغلق فتصير تاريخًا.
 * والأهدافُ تُكتب لصاحبها بمقياسها ومستهدَفها، فتُقرأ مع تقييمه لا بعده.
 *
 * ## وما لا يقع هنا
 *
 * لا يُرقَّى أحدٌ ولا تُصرف مكافأةٌ من هذه الشاشة. التوصيةُ تُكتب في
 * التقييم ويقرؤها من يملك القرار فيفعلها في بابها: الترقيةُ تغييرُ وظيفةٍ
 * ودرجةٍ في ملفّ الموظّف، والعلاوةُ تعديلُ أجره الأساسيّ. وزرٌّ هنا اسمُه
 * «رقِّه» يعني أن يرفع نصٌّ مكتوبٌ في خانةٍ راتبَ رجلٍ بلا أن يقرّه أحد.
 *
 * ولا تقييمَ ذاتيًّا ولا بثلاثمئةٍ وستّين درجة: التقييمُ واحدٌ لكلّ موظّفٍ
 * في الدورة يكتبه مديرُه، ولصاحبه أن يقرأه ويُقرّه ويعلّق.
 */
export function Performance() {
  const { atLeast } = useAuth();
  const canEdit = atLeast('ADMIN');
  const [tab, setTab] = useState<Tab>('cycles');

  const summary = useQuery({
    queryKey: ['performance', 'summary'],
    queryFn: () => api<Summary>('/hr/performance/summary'),
  });

  const s = summary.data;

  return (
    <div className="page">
      <PageHead
        title="إدارة الأداء"
        subtitle="أهدافٌ تُكتب بمقياسها، ودورةٌ تُقاس بمعاييرها، وتقييمٌ يقرؤه صاحبُه، وخطّةٌ لمن احتاجها"
      />

      {summary.isError && (
        <ErrorState
          error={summary.error}
          onRetry={() => void summary.refetch()}
        />
      )}

      {s && (
        <div className="stats">
          <Stat
            label="دورات التقييم"
            value={num(s.cycles)}
            note={
              s.progress ? `«${s.progress.name}» مفتوحة` : 'لا دورةَ مفتوحة'
            }
          />
          <Stat
            label="التقييمات"
            value={num(s.reviews)}
            note={
              s.progress
                ? `${num(s.progress.submitted)} أُرسل في الدورة المفتوحة`
                : 'في كل الدورات'
            }
          />
          <Stat
            label="أهدافٌ جارية"
            value={num(s.openGoals)}
            note="لم تُحسم بعد"
          />
          <Stat
            label="خطط تحسينٍ جارية"
            value={num(s.openPlans)}
            note={s.openPlans > 0 ? 'تُراجَع عند نهاية مدّتها' : 'لا خطط'}
          />
        </div>
      )}

      {/*
        تقدّمُ الدورة يُقال بمن بقي لا بنسبةٍ وحدَها: «سبعون بالمئة» لا
        تقول لمدير الموارد البشرية من يطارد غدًا.
      */}
      {s?.progress && s.progress.expected > s.progress.submitted && (
        <Notice title={`الدورة المفتوحة: ${s.progress.name}`} icon="clock">
          أُرسل {num(s.progress.submitted)} من {num(s.progress.expected)}{' '}
          تقييمًا منتظَرًا — وبقي{' '}
          {plural(s.progress.expected - s.progress.submitted, WORDS.review)}.{' '}
          <Link
            className="link"
            to={`/hr/performance/cycles/${s.progress.cycleId}`}
          >
            افتح الدورة
          </Link>
          .
        </Notice>
      )}

      <div className="tabs" role="tablist" aria-label="أقسام إدارة الأداء">
        {TABS.map((t) => (
          <button
            key={t.key}
            className="tab"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'cycles' && <CyclesSection canEdit={canEdit} />}
      {tab === 'goals' && <GoalsSection canEdit={canEdit} />}
      {tab === 'plans' && <PlansSection canEdit={canEdit} />}
    </div>
  );
}

/* ==========================================================================
   الدورات
   ========================================================================== */

function CyclesSection({ canEdit }: { canEdit: boolean }) {
  const [writing, setWriting] = useState(false);

  const list = useQuery({
    queryKey: ['performance', 'cycles'],
    queryFn: () => api<CycleRow[]>('/hr/performance/cycles'),
  });

  const rows = list.data ?? [];

  return (
    <div className="card mt-3">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          دورات التقييم
        </h2>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setWriting(true)}>
            <Icon name="plus" size={15} />
            دورة جديدة
          </button>
        )}
      </div>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا دورات"
          text="الدورةُ مدّةٌ يُقاس فيها الأداءُ ومعاييرُ يُقاس بها. تُعدّ أوّلًا فتُحرَّر معاييرُها، ثمّ تُفتح فتُجمَّد وتُكتب فيها التقييمات."
          action={
            canEdit ? (
              <button
                className="btn btn-primary"
                onClick={() => setWriting(true)}
              >
                افتح أوّل دورة
              </button>
            ) : undefined
          }
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الدورة</th>
                <th>المدّة</th>
                <th>المعايير</th>
                <th>السُّلَّم</th>
                <th>التقييمات</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link
                      className="link"
                      to={`/hr/performance/cycles/${c.id}`}
                    >
                      {c.name}
                    </Link>
                    {c.dueOn && (
                      <div className="tiny mute">آخر موعد: {date(c.dueOn)}</div>
                    )}
                  </td>
                  <td className="tiny">
                    {date(c.startsOn ?? '')} — {date(c.endsOn ?? '')}
                  </td>
                  <td className="num">{num(c.criteriaCount)}</td>
                  <td className="num">{num(c.scaleMax)}</td>
                  <td className="num">{num(c.reviews)}</td>
                  <td>
                    <Badge kind={c.statusKind}>{c.statusLabel}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {writing && <CycleDialog onClose={() => setWriting(false)} />}
    </div>
  );
}

/* ==========================================================================
   نافذة الدورة
   ========================================================================== */

interface CriterionDraft {
  key: string;
  label: string;
  weight: string;
  description: string;
}

const emptyCriterion = (): CriterionDraft => ({
  key: '',
  label: '',
  weight: '0',
  description: '',
});

function CycleDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const today = new Date().toISOString().slice(0, 10);
  const [name, setName] = useState('');
  const [startsOn, setStartsOn] = useState(`${today.slice(0, 4)}-01-01`);
  const [endsOn, setEndsOn] = useState(`${today.slice(0, 4)}-12-31`);
  const [dueOn, setDueOn] = useState('');
  const [scaleMax, setScaleMax] = useState('5');
  const [notes, setNotes] = useState('');
  const [criteria, setCriteria] = useState<CriterionDraft[]>(
    SUGGESTED_CRITERIA.map((c) => ({
      key: c.key,
      label: c.label,
      weight: String(c.weight),
      description: c.description ?? '',
    })),
  );

  const weightTotal = criteria.reduce((n, c) => n + (Number(c.weight) || 0), 0);
  const filled = criteria.filter((c) => c.key.trim() && c.label.trim());

  const save = useMutation({
    mutationFn: () =>
      api('/hr/performance/cycles', {
        method: 'POST',
        body: {
          name,
          startsOn,
          endsOn,
          dueOn: dueOn || null,
          scaleMax: Number(scaleMax),
          notes: notes || null,
          criteria: filled.map((c) => ({
            key: c.key.trim().toUpperCase(),
            label: c.label.trim(),
            weight: Number(c.weight) || 0,
            description: c.description.trim() || undefined,
          })),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['performance'] });
      toast('كُتبت الدورة — افتحها حين تنتهي من معاييرها.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title="دورة تقييم جديدة"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || name.trim().length < 2}
            onClick={() => save.mutate()}
          >
            احفظ
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="pc-name">
            اسم الدورة
          </label>
          <input
            id="pc-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="تقييم ٢٠٢٦ السنوي"
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="pc-start">
            بداية المدّة المقيسة
          </label>
          <input
            id="pc-start"
            className="input"
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="pc-end">
            نهايتها
          </label>
          <input
            id="pc-end"
            className="input"
            type="date"
            value={endsOn}
            onChange={(e) => setEndsOn(e.target.value)}
          />
          <span className="tiny mute">
            هذه مدّةُ الأداء الذي يُقاس، لا مدّةُ كتابة التقييمات.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="pc-due">
            آخر موعدٍ لكتابة التقييمات
          </label>
          <input
            id="pc-due"
            className="input"
            type="date"
            value={dueOn}
            onChange={(e) => setDueOn(e.target.value)}
          />
          {/*
            الموعدُ يُقرأ ولا يمنع: تقييمٌ يُردّ لأنّه تأخّر يومًا لا يُكتب
            أبدًا، ويبقى الرجلُ بلا تقييمٍ في ملفّه.
          */}
          <span className="tiny mute">
            يُقرأ في المتابعة ولا يمنع الكتابةَ بعده.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="pc-scale">
            أعلى درجة في السُّلَّم
          </label>
          <input
            id="pc-scale"
            className="input num"
            type="number"
            min={3}
            max={10}
            value={scaleMax}
            onChange={(e) => setScaleMax(e.target.value)}
          />
          <span className="tiny mute">
            خمسةٌ أو عشرة بحسب ما اعتادته الشركة — والمقارنةُ بين الدورات تكون
            بالنسبة لا بالدرجة.
          </span>
        </div>

        <div className="field span-2">
          <div className="row" style={{ gap: 'var(--sp-2)' }}>
            <span className="label" style={{ flex: 1 }}>
              المعايير
            </span>
            <span className="tiny mute">
              مجموع الأوزان: {num(weightTotal)}٪
            </span>
            <button
              className="btn btn-sm"
              type="button"
              onClick={() => setCriteria([...criteria, emptyCriterion()])}
            >
              <Icon name="plus" size={13} />
              معيار
            </button>
          </div>

          {/*
            الأوزانُ تُقال ولا تُفرض على المئة: شركةٌ لا تزن معاييرَها تترك
            الأصفارَ فيُؤخذ المتوسّطُ البسيط — ومنعُها من الحفظ حتى تبلغ مئةً
            حسابٌ يُفرض على من لم يطلبه.
          */}
          {weightTotal !== 100 && weightTotal !== 0 && (
            <span className="tiny mute">
              المجموعُ ليس مئةً — والمحصّلةُ تُقسَم على مجموع الأوزان لا على
              مئة، فالنتيجةُ صحيحةٌ على كلّ حال.
            </span>
          )}

          <div className="table-wrap mt-2">
            <table className="table">
              <thead>
                <tr>
                  <th>المفتاح</th>
                  <th>الاسم</th>
                  <th>الوزن ٪</th>
                  <th>ما يُقاس به</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {criteria.map((c, i) => (
                  <tr key={i}>
                    <td>
                      <input
                        className="input"
                        value={c.key}
                        onChange={(e) => {
                          const next = [...criteria];
                          next[i] = { ...c, key: e.target.value.toUpperCase() };
                          setCriteria(next);
                        }}
                        placeholder="QUALITY"
                      />
                    </td>
                    <td>
                      <input
                        className="input"
                        value={c.label}
                        onChange={(e) => {
                          const next = [...criteria];
                          next[i] = { ...c, label: e.target.value };
                          setCriteria(next);
                        }}
                      />
                    </td>
                    <td style={{ width: 90 }}>
                      <input
                        className="input num"
                        type="number"
                        min={0}
                        max={100}
                        value={c.weight}
                        onChange={(e) => {
                          const next = [...criteria];
                          next[i] = { ...c, weight: e.target.value };
                          setCriteria(next);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        className="input"
                        value={c.description}
                        onChange={(e) => {
                          const next = [...criteria];
                          next[i] = { ...c, description: e.target.value };
                          setCriteria(next);
                        }}
                      />
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-ghost"
                        type="button"
                        aria-label="احذف المعيار"
                        onClick={() =>
                          setCriteria(criteria.filter((_, j) => j !== i))
                        }
                      >
                        <Icon name="trash" size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <span className="tiny mute">
            المفتاحُ حروفٌ إنجليزيّةٌ كبيرة، ولا يتغيّر بعد فتح الدورة: درجاتُ
            التقييمات مكتوبةٌ به، وتغييرُه يترك أرقامًا لا يُعرف ما تقيس.
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="pc-notes">
            ملاحظات
          </label>
          <textarea
            id="pc-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="ما يحتاج من يقرأ الدورةَ بعد سنةٍ أن يعرفه"
          />
        </div>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   الأهداف
   ========================================================================== */

function GoalsSection({ canEdit }: { canEdit: boolean }) {
  const [status, setStatus] = useState('');
  const [writing, setWriting] = useState(false);
  const [editing, setEditing] = useState<GoalRow | null>(null);

  const query = { status: status || undefined };

  const list = useQuery({
    queryKey: ['performance', 'goals', query],
    queryFn: () => api<GoalRow[]>(`/hr/performance/goals${qs(query)}`),
  });

  const rows = list.data ?? [];
  const overdue = rows.filter((g) => g.overdue).length;

  return (
    <div className="card mt-3">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          الأهداف
        </h2>
        <select
          className="select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">كل الحالات</option>
          {GOAL_STATUSES.map((k) => (
            <option key={k} value={k}>
              {GOAL_STATUS_MAP[k].label}
            </option>
          ))}
        </select>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setWriting(true)}>
            <Icon name="plus" size={15} />
            هدف
          </button>
        )}
      </div>

      {overdue > 0 && (
        <Notice title="أهدافٌ مضى موعدُها" icon="alert" kind="warning">
          {plural(overdue, WORDS.goal)} جاريةٌ مضى موعدُها ولم تُحسم. والهدفُ
          الذي لا يُحسم لا يُقاس عليه أحد.
        </Notice>
      )}

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا أهداف"
          text="الهدفُ يُكتب بمقياسه: «يحسّن الجودة» جملةٌ لا يُحكم عليها، و«يخفض إعادةَ العمل من عشرين إلى عشرة بالمئة» هدفٌ يُقال فيه نعم أو لا."
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الهدف</th>
                <th>صاحبُه</th>
                <th>المقياس والمستهدَف</th>
                <th>الوزن</th>
                <th>ما بلغه</th>
                <th>الموعد</th>
                <th>الحالة</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.id}>
                  <td>
                    {g.title}
                    {g.cycle && <div className="tiny mute">{g.cycle.name}</div>}
                  </td>
                  <td className="tiny">{g.employee.name}</td>
                  <td className="tiny">
                    {g.metric ?? '—'}
                    {g.target ? ` → ${g.target}` : ''}
                  </td>
                  <td className="num">
                    {g.weight > 0 ? `${num(g.weight)}٪` : '—'}
                  </td>
                  <td className="num">{num(g.progress)}٪</td>
                  <td className="tiny">
                    {g.dueOn ? date(g.dueOn) : '—'}
                    {g.overdue && (
                      <div className="tiny" style={{ color: 'var(--warning)' }}>
                        مضى
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge kind={g.statusKind}>{g.statusLabel}</Badge>
                  </td>
                  {canEdit && (
                    <td>
                      <button
                        className="btn btn-sm"
                        onClick={() => setEditing(g)}
                      >
                        تعديل
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(writing || editing) && (
        <GoalDialog
          goal={editing}
          onClose={() => {
            setWriting(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function GoalDialog({
  goal,
  onClose,
}: {
  goal: GoalRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [employeeId, setEmployeeId] = useState(goal?.employee.id ?? '');
  const [cycleId, setCycleId] = useState(goal?.cycle?.id ?? '');
  const [title, setTitle] = useState(goal?.title ?? '');
  const [description, setDescription] = useState(goal?.description ?? '');
  const [metric, setMetric] = useState(goal?.metric ?? '');
  const [target, setTarget] = useState(goal?.target ?? '');
  const [weight, setWeight] = useState(String(goal?.weight ?? 0));
  const [progress, setProgress] = useState(String(goal?.progress ?? 0));
  const [status, setStatus] = useState<GoalStatus>(goal?.status ?? 'OPEN');
  const [dueOn, setDueOn] = useState(goal?.dueOn ?? '');
  const [outcome, setOutcome] = useState(goal?.outcome ?? '');

  const staff = useQuery({
    queryKey: ['employees', { status: 'ACTIVE', pageSize: 200 }],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>(
        '/hr/employees?status=ACTIVE&pageSize=200',
      ),
  });

  const cycles = useQuery({
    queryKey: ['performance', 'cycles'],
    queryFn: () => api<CycleRow[]>('/hr/performance/cycles'),
  });

  const save = useMutation({
    mutationFn: () =>
      api(goal ? `/hr/performance/goals/${goal.id}` : '/hr/performance/goals', {
        method: goal ? 'PATCH' : 'POST',
        body: {
          employeeId,
          cycleId: cycleId || null,
          title,
          description: description || null,
          metric: metric || null,
          target: target || null,
          weight: Number(weight) || 0,
          progress: Number(progress) || 0,
          status,
          dueOn: dueOn || null,
          outcome: outcome || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['performance'] });
      toast('حُفظ الهدف.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={goal ? 'تعديل هدف' : 'هدف جديد'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || !employeeId || title.trim().length < 3}
            onClick={() => save.mutate()}
          >
            احفظ
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="pg-emp">
            صاحب الهدف
          </label>
          <select
            id="pg-emp"
            className="select"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            disabled={goal !== null}
          >
            <option value="">— اختر —</option>
            {(staff.data?.items ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          {goal && (
            <span className="tiny mute">
              صاحبُ الهدف لا يتغيّر — اكتب هدفًا لغيره بدل نقله.
            </span>
          )}
        </div>

        <div className="field">
          <label className="label" htmlFor="pg-cycle">
            الدورة
          </label>
          <select
            id="pg-cycle"
            className="select"
            value={cycleId}
            onChange={(e) => setCycleId(e.target.value)}
          >
            <option value="">— خارج دورة —</option>
            {(cycles.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            هدفٌ في دورةٍ يُقرأ مع تقييم صاحبه فيها.
          </span>
        </div>

        <div className="field span-2">
          <label className="label req" htmlFor="pg-title">
            الهدف
          </label>
          <input
            id="pg-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="يخفض إعادة العمل في مواد الحملات"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="pg-metric">
            بماذا يُقاس
          </label>
          <input
            id="pg-metric"
            className="input"
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            placeholder="نسبة المواد المعادة"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="pg-target">
            المستهدَف
          </label>
          <input
            id="pg-target"
            className="input"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="١٠٪ بدل ٢٠٪"
          />
          {/*
            المقياسُ والمستهدَفُ حقلان لا كلمتان في الوصف: هدفٌ بلا رقمٍ
            يُحكم عليه بالرأي، وهو عين ما يشتكي منه من يُقيَّم.
          */}
          <span className="tiny mute">
            بلا مقياسٍ ومستهدَفٍ يصير الحكمُ رأيًا لا قياسًا.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="pg-weight">
            الوزن ٪
          </label>
          <input
            id="pg-weight"
            className="input num"
            type="number"
            min={0}
            max={100}
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="pg-progress">
            ما بلغه ٪
          </label>
          <input
            id="pg-progress"
            className="input num"
            type="number"
            min={0}
            max={100}
            value={progress}
            onChange={(e) => setProgress(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="pg-due">
            الموعد
          </label>
          <input
            id="pg-due"
            className="input"
            type="date"
            value={dueOn}
            onChange={(e) => setDueOn(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="pg-status">
            الحالة
          </label>
          <select
            id="pg-status"
            className="select"
            value={status}
            onChange={(e) => setStatus(e.target.value as GoalStatus)}
          >
            {GOAL_STATUSES.map((k) => (
              <option key={k} value={k}>
                {GOAL_STATUS_MAP[k].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            {GOAL_STATUS_MAP[status].description}
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="pg-desc">
            تفصيل
          </label>
          <textarea
            id="pg-desc"
            className="input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {status !== 'OPEN' && (
          <div className="field span-2">
            <label className="label" htmlFor="pg-outcome">
              لماذا انتهى إلى هذا
            </label>
            <textarea
              id="pg-outcome"
              className="input"
              rows={2}
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ==========================================================================
   خطط التحسين
   ========================================================================== */

function PlansSection({ canEdit }: { canEdit: boolean }) {
  const [status, setStatus] = useState('');
  const [writing, setWriting] = useState(false);
  const [editing, setEditing] = useState<PlanRow | null>(null);
  const [closing, setClosing] = useState<PlanRow | null>(null);

  const query = { status: status || undefined };

  const list = useQuery({
    queryKey: ['performance', 'plans', query],
    queryFn: () => api<PlanRow[]>(`/hr/performance/plans${qs(query)}`),
  });

  const rows = list.data ?? [];

  return (
    <div className="card mt-3">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          خطط تحسين الأداء
        </h2>
        <select
          className="select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">كل الحالات</option>
          {PLAN_STATUSES.map((k) => (
            <option key={k} value={k}>
              {PLAN_STATUS_MAP[k].label}
            </option>
          ))}
        </select>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setWriting(true)}>
            <Icon name="plus" size={15} />
            خطّة
          </button>
        )}
      </div>

      <Notice title="ما تكونه الخطّة وما لا تكونه" icon="info">
        مدّةٌ محدّدةٌ وأهدافٌ مكتوبةٌ ومراجعةٌ في نهايتها. وبلا مدّةٍ ولا مكتوبٍ
        تصير تهديدًا مفتوحًا لا إجراءً — ولا تصلح سندًا في نزاع. والخطّةُ
        الجاريةُ واحدةٌ للرجل الواحد.
      </Notice>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا خطط"
          text="تُفتح لمن كان أداؤه دون المطلوب — بمدّةٍ وأهدافٍ مكتوبةٍ يُقاس عليها."
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الموظّف</th>
                <th>السبب</th>
                <th>المدّة</th>
                <th>البنود</th>
                <th>يتابعها</th>
                <th>الحالة</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td>{p.employee.name}</td>
                  <td className="tiny">{p.reason}</td>
                  <td className="tiny">
                    {date(p.startsOn ?? '')} — {date(p.endsOn ?? '')}
                    {p.live && p.daysLeft !== null && (
                      <div
                        className="tiny"
                        style={
                          p.daysLeft <= 0
                            ? { color: 'var(--warning)' }
                            : undefined
                        }
                      >
                        {p.daysLeft > 0
                          ? `بقي ${num(p.daysLeft)} يومًا`
                          : 'مضت مدّتُها'}
                      </div>
                    )}
                  </td>
                  <td className="num">
                    {num(p.done)} / {num(p.objectives.length)}
                  </td>
                  <td className="tiny">{p.owner?.name ?? '—'}</td>
                  <td>
                    <Badge kind={p.statusKind}>{p.statusLabel}</Badge>
                  </td>
                  {canEdit && (
                    <td>
                      <div className="row" style={{ gap: 4 }}>
                        {p.live && (
                          <>
                            <button
                              className="btn btn-sm"
                              onClick={() => setEditing(p)}
                            >
                              تعديل
                            </button>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => setClosing(p)}
                            >
                              أغلقها
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(writing || editing) && (
        <PlanDialog
          plan={editing}
          onClose={() => {
            setWriting(false);
            setEditing(null);
          }}
        />
      )}
      {closing && (
        <ClosePlanDialog plan={closing} onClose={() => setClosing(null)} />
      )}
    </div>
  );
}

function PlanDialog({
  plan,
  onClose,
}: {
  plan: PlanRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const today = new Date().toISOString().slice(0, 10);
  const in90 = new Date(Date.now() + 90 * 86_400_000)
    .toISOString()
    .slice(0, 10);

  const [employeeId, setEmployeeId] = useState(plan?.employee.id ?? '');
  const [ownerId, setOwnerId] = useState(plan?.owner?.id ?? '');
  const [reason, setReason] = useState(plan?.reason ?? '');
  const [startsOn, setStartsOn] = useState(plan?.startsOn ?? today);
  const [endsOn, setEndsOn] = useState(plan?.endsOn ?? in90);
  const [objectives, setObjectives] = useState<
    { text: string; done: boolean }[]
  >(plan?.objectives ?? [{ text: '', done: false }]);

  const staff = useQuery({
    queryKey: ['employees', { status: 'ACTIVE', pageSize: 200 }],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>(
        '/hr/employees?status=ACTIVE&pageSize=200',
      ),
  });

  const filled = objectives.filter((o) => o.text.trim().length >= 3);

  const save = useMutation({
    mutationFn: () =>
      api(plan ? `/hr/performance/plans/${plan.id}` : '/hr/performance/plans', {
        method: plan ? 'PATCH' : 'POST',
        body: plan
          ? {
              ownerId: ownerId || null,
              reason,
              startsOn,
              endsOn,
              objectives: filled,
            }
          : {
              employeeId,
              ownerId: ownerId || null,
              reason,
              startsOn,
              endsOn,
              objectives: filled,
            },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['performance'] });
      toast('حُفظت الخطّة.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={plan ? 'تعديل خطّة' : 'خطّة تحسين جديدة'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={
              save.isPending ||
              (!plan && !employeeId) ||
              reason.trim().length < 10
            }
            onClick={() => save.mutate()}
          >
            احفظ
          </button>
        </>
      }
    >
      <div className="form-grid">
        {!plan && (
          <div className="field">
            <label className="label req" htmlFor="pp-emp">
              الموظّف
            </label>
            <select
              id="pp-emp"
              className="select"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            >
              <option value="">— اختر —</option>
              {(staff.data?.items ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="field">
          <label className="label" htmlFor="pp-owner">
            من يتابعها
          </label>
          <select
            id="pp-owner"
            className="select"
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
          >
            <option value="">— بلا متابع —</option>
            {(staff.data?.items ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label req" htmlFor="pp-start">
            بدايتها
          </label>
          <input
            id="pp-start"
            className="input"
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="pp-end">
            المراجعة في
          </label>
          <input
            id="pp-end"
            className="input"
            type="date"
            value={endsOn}
            onChange={(e) => setEndsOn(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label req" htmlFor="pp-reason">
            السبب
          </label>
          <textarea
            id="pp-reason"
            className="input"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="ما الذي وقع بالضبط، ومتى، وما أثرُه — لا وصفًا لشخصه"
          />
          <span className="tiny mute">
            يُقرأ بعد شهرين ممّن لم يحضر الحديث — فاكتبه كما يُقرأ حينها.
          </span>
        </div>

        <div className="field span-2">
          <div className="row" style={{ gap: 'var(--sp-2)' }}>
            <span className="label" style={{ flex: 1 }}>
              ما يُنتظر بلوغُه
            </span>
            <button
              className="btn btn-sm"
              type="button"
              disabled={objectives.length >= MAX_PLAN_OBJECTIVES}
              onClick={() =>
                setObjectives([...objectives, { text: '', done: false }])
              }
            >
              <Icon name="plus" size={13} />
              بند
            </button>
          </div>

          {objectives.map((o, i) => (
            <div key={i} className="row mt-1" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={o.done}
                aria-label="تحقّق"
                onChange={(e) => {
                  const next = [...objectives];
                  next[i] = { ...o, done: e.target.checked };
                  setObjectives(next);
                }}
              />
              <input
                className="input"
                style={{ flex: 1 }}
                value={o.text}
                onChange={(e) => {
                  const next = [...objectives];
                  next[i] = { ...o, text: e.target.value };
                  setObjectives(next);
                }}
                placeholder="يسلّم ثلاث حملات في موعدها بلا إعادة"
              />
              <button
                className="btn btn-sm btn-ghost"
                type="button"
                aria-label="احذف البند"
                onClick={() =>
                  setObjectives(objectives.filter((_, j) => j !== i))
                }
              >
                <Icon name="trash" size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

function ClosePlanDialog({
  plan,
  onClose,
}: {
  plan: PlanRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<PlanStatus>('MET');
  const [outcome, setOutcome] = useState('');

  const close = useMutation({
    mutationFn: () =>
      api(`/hr/performance/plans/${plan.id}/close`, {
        method: 'POST',
        body: { status, outcome },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['performance'] });
      toast('أُغلقت الخطّة.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={`إغلاق خطّة ${plan.employee.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={close.isPending || outcome.trim().length < 10}
            onClick={() => close.mutate()}
          >
            أغلقها
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label" htmlFor="cp-status">
            إلى ماذا انتهت
          </label>
          <select
            id="cp-status"
            className="select"
            value={status}
            onChange={(e) => setStatus(e.target.value as PlanStatus)}
          >
            {PLAN_STATUSES.filter((k) => k !== 'OPEN').map((k) => (
              <option key={k} value={k}>
                {PLAN_STATUS_MAP[k].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            {PLAN_STATUS_MAP[status].description}
          </span>
        </div>

        <div className="field span-2">
          <label className="label req" htmlFor="cp-outcome">
            الخلاصة
          </label>
          <textarea
            id="cp-outcome"
            className="input"
            rows={3}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            placeholder="ما بلغه وما لم يبلغه، وما بُني على ذلك"
          />
          {/*
            الخلاصةُ مطلوبةٌ لأنّ الخطّةَ مستند: من يقرؤها بعد شهرين — أو في
            نزاع — يحتاج أن يعرف لماذا انتهت إلى ما انتهت إليه.
          */}
          <span className="tiny mute">
            الخطّةُ مستندٌ يُسأل عنه — وإغلاقُها بكلمةٍ واحدةٍ لا يصلح سندًا.
          </span>
        </div>

        {status === 'FAILED' && (
          <div className="field span-2">
            <Notice title="وما بعدها قرارٌ في بابه" icon="alert" kind="warning">
              هذا النظامُ يسجّل أنّ الخطّةَ لم تتحقّق ولا يفعل شيئًا بعدها:
              إنهاءُ الخدمة يقع في ملفّ الموظّف، وهو قرارٌ يُتّخذ بيدٍ ويُوقَّع
              عليه — لا نتيجةٌ آليّةٌ لخانةٍ تُختار هنا.
            </Notice>
          </div>
        )}
      </div>
    </Modal>
  );
}
